import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import GameScreen from '@/components/GameScreen/GameScreen';
import StatisticsDashboard from '@/components/Statistics/StatisticsDashboard';
import AdminDashboard from '@/components/Admin/AdminDashboard';
import { LoginPage, AuthProvider } from '@/components/Auth';
import { useFirebaseUser } from '@/hooks/useFirebase';
import { SessionStats, AppError, FingerType, TrainingMode, HandMode, CustomTrainingConfig, TrainingSystemState } from '@/types';
// 🎯 PHASE 2: Hand Picker Integration - Now only used in GameScreen

import { COMPLETE_TRAINING_COURSE } from '@/data/courseStructure';
// 🔧 PHASE 1 FIX: Import session context management
import {
  SessionContextManager,
  CompletionStateManager
} from '@/utils/sessionContextManager';
import GuitarHeroGame from '@/components/GuitarHero/GuitarHeroGame';

/**
 * 🎯 PHASE 2: Helper function to derive handMode from selectedFingers
 */
const deriveHandMode = (fingers: FingerType[]): HandMode => {
  const hasLeft = fingers.some(f => f.startsWith('left'));
  const hasRight = fingers.some(f => f.startsWith('right'));
  if (hasLeft && hasRight) return 'both';
  if (hasLeft) return 'left';
  if (hasRight) return 'right';
  return 'both'; // default
};

interface AppState {
  isInitialized: boolean;
  errors: AppError[];

  // SPRINT 1: Dual-mode training system state
  trainingSystem: TrainingSystemState;

  // 🔧 ADMIN: Admin panel state
  showAdminPanel: boolean;
}

// Default training system state
const getDefaultTrainingSystemState = (): TrainingSystemState => ({
  mode: 'custom', // Start with custom mode

  customConfig: {
    selectedFingers: ['leftPinky', 'rightPinky'], // Default: both pinkies
    handMode: 'both',
    complexityMode: 1
  },

  courseProgress: {
    currentLevel: 1,
    currentDay: 1, // FIXED: Start at Day 1
    currentRound: 1, // FIXED: Start at Round 1
    dayProgress: COMPLETE_TRAINING_COURSE.map(day => ({
      dayNumber: day.dayNumber,
      unlocked: day.dayNumber === 1, // Only day 1 unlocked initially
      completed: false,
      rounds: Array.from({ length: 5 }, (_, i) => ({
        roundNumber: i + 1,
        unlocked: i === 0, // Only round 1 unlocked
        completed: false,
        attempts: 0
      }))
    })),
    unlockedDays: [1], // FIXED: Only day 1 unlocked initially
    completedDays: [],
    levelProgress: {},
    lastUpdated: Date.now()
  },

  activeSession: null
});

/**
 * 🔒 SECURITY: Get user-specific localStorage key
 * Prevents cross-user data contamination
 */
const getLocalStorageKey = (uid: string | null): string => {
  if (uid) {
    return `keyboardhero-training-system-${uid}`;
  }
  return 'keyboardhero-training-system-anonymous';
};

const INITIAL_STATE: AppState = {
  isInitialized: false,
  errors: [],
  trainingSystem: getDefaultTrainingSystemState(),
  showAdminPanel: false
};




/**
 * Main App Component with Firebase Authentication
 * 
 * Wraps the entire application with authentication providers and handles
 * the authentication flow with proper loading states and error handling.
 */

// Internal authenticated app component
const AuthenticatedApp: React.FC = () => {
  // 🔥 RE-ENABLED: Firebase user service with error handling
  const { firebaseUser, loading: userLoading, error: userError } = useFirebaseUser();
  const [appState, setAppState] = useState<AppState>(INITIAL_STATE);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [appMode, setAppMode] = useState<'training' | 'game'>('training');
  const [showFingerHints, setShowFingerHints] = useState(false); // CHANGED: Default to false (hints off)
  const [sessionStatus, setSessionStatus] = useState<'ready' | 'active' | 'completed'>('ready');
  // 🔧 SPRINT 2 TASK 2.1: Removed isSessionActive state (bidirectional sync eliminated)
  // 🔧 SPRINT 1 TASK 1.2: Use direct ref instead of state to prevent loops
  const gameScreenRef = useRef<any>(null);
  // 🔧 PHASE 1 FIX: Use centralized completion state management
  const sessionContextManager = SessionContextManager.getInstance();
  const completionStateManager = CompletionStateManager.getInstance();
  // 🔧 SPRINT 3 TASK 3.1: Add refs for batched updates and completion optimization
  const completionInProgressRef = useRef(false);
  const pendingCompletionRef = useRef<any>(null);

  // 🔧 SPRINT 3 TASK 3.2: Add debounced state update refs
  const updateTimeoutRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<((prev: TrainingSystemState) => TrainingSystemState) | null>(null);

  const currentAppStateRef = useRef<AppState>();
  currentAppStateRef.current = appState;

  // 🔧 SPRINT 1 TASK 1.1: Add refs to prevent initialization loops
  const initializationCompletedRef = useRef(false);
  const firebaseUserProcessedRef = useRef<string | null>(null);

  const location = useLocation();
  const isStatisticsPage = location.pathname === '/statistics';
  // const isAdminPage = appState.showAdminPanel; // Admin is overlay, not route

  // 🔧 SPRINT 1 TASK 1.2: Direct ref assignment without state updates
  const handleGameScreenRef = useCallback((ref: any) => {
    gameScreenRef.current = ref; // Direct ref assignment - no state updates
    console.log('📌 GameScreen ref assigned:', !!ref);
  }, []);

  // 🔧 SPRINT 2 TASK 2.1: Updated game control functions to check sessionStatus instead of isSessionActive
  const handlePauseResumeFixed = () => {
    console.log('🎯 Header Pause/Resume clicked', { gameScreenRef: !!gameScreenRef.current, sessionStatus });
    if (gameScreenRef.current?.handlePauseResume) {
      gameScreenRef.current.handlePauseResume();
    } else {
      console.warn('❌ GameScreen ref not available for pause/resume');
    }
  };

  const handleStopSessionFixed = () => {
    console.log('🛑 Header Stop clicked', { gameScreenRef: !!gameScreenRef.current });
    if (gameScreenRef.current?.handleEndSession) {
      gameScreenRef.current.handleEndSession();
    } else {
      console.warn('❌ GameScreen ref not available for stop');
    }
  };

  /**
   * 🔧 SPRINT 1 TASK 1.1: One-time app initialization - NO FIREBASE DEPENDENCIES
   */
  useEffect(() => {
    // Only initialize once
    if (initializationCompletedRef.current) {
      return;
    }

    const initializeApp = async () => {
      try {
        console.log('🚀 KeyboardHero one-time initialization starting...');

        // Load saved training system state from localStorage
        const savedState = loadTrainingSystemState();
        if (savedState) {
          setAppState(prev => ({
            ...prev,
            trainingSystem: savedState
          }));
          console.log('Loaded saved training system state:', savedState);
        }

        // Set up error handling (one-time setup)
        const handleGlobalError = (error: ErrorEvent) => {
          console.error('Global error:', error);
          addErrorInternal('global', error.message);
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
          console.error('Unhandled promise rejection:', event.reason);
          addErrorInternal('promise', event.reason?.message || 'Promise rejection');
        };

        window.addEventListener('error', handleGlobalError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        // Mark as initialized
        setAppState(prev => ({ ...prev, isInitialized: true }));
        initializationCompletedRef.current = true;

        console.log('✅ KeyboardHero base initialization completed');

        // Cleanup on unmount
        return () => {
          window.removeEventListener('error', handleGlobalError);
          window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
      } catch (error) {
        console.error('Failed to initialize KeyboardHero:', error);
        addErrorInternal('initialization', 'Failed to initialize application');
      }
    };

    initializeApp();
  }, []); // 🔧 SPRINT 1: Empty dependencies - no loops!

  /**
   * 🔧 SPRINT 1 TASK 1.1: Separate Firebase user state handling - STABLE DEPENDENCIES
   */
  useEffect(() => {
    // Only process Firebase user changes after base initialization
    if (!initializationCompletedRef.current) {
      return;
    }

    // Avoid processing the same user multiple times
    const currentUserId = firebaseUser?.uid || null;
    if (firebaseUserProcessedRef.current === currentUserId) {
      return;
    }

    console.log('🔥 Processing Firebase user state change:', {
      previousUser: firebaseUserProcessedRef.current,
      currentUser: currentUserId
    });

    if (firebaseUser?.uid) {
      console.log('🔥 Firebase user authenticated:', firebaseUser.uid);

      // 🧹 Clear old localStorage data when user changes
      const currentKey = getLocalStorageKey(firebaseUser.uid);
      const allKeys = Object.keys(localStorage);

      // Remove all old keyboardhero training keys that don't match current user
      allKeys.forEach(key => {
        if (key.startsWith('keyboardhero-training-system-') && key !== currentKey) {
          console.log('🧹 Clearing stale localStorage for different user:', key);
          localStorage.removeItem(key);
        }
      });

      // Also clear the old anonymous key if it exists
      if (localStorage.getItem('keyboardhero-training-system-anonymous')) {
        console.log('🧹 Clearing anonymous localStorage');
        localStorage.removeItem('keyboardhero-training-system-anonymous');
      }

      // Also clear the legacy key without UID if it exists
      if (localStorage.getItem('keyboardhero-training-system')) {
        console.log('🧹 Clearing legacy localStorage (no UID)');
        localStorage.removeItem('keyboardhero-training-system');
      }

      // 🔄 Reload training state for the new user (after clearing old data)
      const reloadedState = loadTrainingSystemState();
      if (reloadedState) {
        setAppState(prev => ({
          ...prev,
          trainingSystem: reloadedState
        }));
        console.log('✅ Reloaded training state for user:', firebaseUser.uid);
      }

      // 🎓 Initialize course progress via centralized Manager
      const initProgress = async () => {
        try {
          const CourseProgressManager = (await import('@/services/CourseProgressManager')).default;
          const manager = CourseProgressManager.getInstance();
          const progress = await manager.init(firebaseUser.uid);

          setAppState(prev => ({
            ...prev,
            trainingSystem: {
              ...prev.trainingSystem,
              courseProgress: progress
            }
          }));
          console.log('✅ Course progress initialized via Manager');
        } catch (error) {
          console.error('🚫 Error initializing course progress:', error);
        }
      };

      initProgress();
      firebaseUserProcessedRef.current = firebaseUser.uid;
    } else if (userError) {
      console.warn('⚠️ Firebase user error (non-critical):', userError.message);
      // 🔧 FIXED: Don't treat Firebase errors as critical - app should work offline
      // Only log the error, don't add to error state as it's not critical for core functionality
      firebaseUserProcessedRef.current = null;
    } else {
      console.log('🔥 No Firebase user or user signed out');
      firebaseUserProcessedRef.current = null;
    }
  }, [firebaseUser?.uid, userError]); // 🔧 SPRINT 1: Minimal, stable dependencies

  /**
   * 🔧 SPRINT 3 TASK 3.3: Cleanup function for completion optimization refs
   */
  useEffect(() => {
    return () => {
      // Cleanup completion refs
      completionInProgressRef.current = false;
      pendingCompletionRef.current = null;

      // Cleanup debounced update refs  
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
      pendingUpdateRef.current = null;

      console.log('📝 SPRINT 3: Cleaned up completion optimization refs');
    };
  }, []);

  /**
   * Save training system state to localStorage
   * 🔒 SECURITY: Uses user-specific keys to prevent cross-user contamination
   */
  const saveTrainingSystemState = (state: TrainingSystemState) => {
    try {
      const storageKey = getLocalStorageKey(firebaseUser?.uid || null);
      const dataToSave = {
        ...state,
        uid: firebaseUser?.uid || null, // Store UID for validation
        lastUpdated: Date.now(),
        version: '1.0.0'
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      console.log('Training system state saved for user:', firebaseUser?.uid);
    } catch (error) {
      console.error('Failed to save training system state:', error);
      addErrorInternal('data', 'Failed to save progress');
    }
  };

  /**
   * Add an error to the error list (INTERNAL)
   */
  const addErrorInternal = (type: AppError['type'], message: string) => {
    const newError: AppError = {
      id: `error-${Date.now()}-${Math.random()}`,
      type,
      message,
      timestamp: Date.now()
    };

    setAppState(prev => ({
      ...prev,
      errors: [...prev.errors.slice(-9), newError]
    }));
  };

  // validateCourseProgress — REMOVED: validation is now inside CourseProgressManager

  /**
   * Load training system state from localStorage.
   * NOTE: Course progress is now managed by CourseProgressManager.
   * This only loads non-progress config (mode, customConfig, etc).
   */
  const loadTrainingSystemState = (): TrainingSystemState | null => {
    try {
      const storageKey = getLocalStorageKey(firebaseUser?.uid || null);
      const saved = localStorage.getItem(storageKey);

      if (saved) {
        const parsed = JSON.parse(saved);

        // Validate UID
        if (firebaseUser?.uid && parsed.uid !== firebaseUser.uid) {
          localStorage.removeItem(storageKey);
          return null;
        }

        const defaultState = getDefaultTrainingSystemState();
        return {
          ...defaultState,
          mode: parsed.mode || defaultState.mode,
          customConfig: parsed.customConfig || defaultState.customConfig,
          // courseProgress will be set by CourseProgressManager.init()
          courseProgress: defaultState.courseProgress,
          activeSession: null
        };
      }
    } catch (error) {
      console.error('Failed to load training system state:', error);
    }
    return null;
  };



  /**
   * Update training system state and save
   */
  const updateTrainingSystemState = (updater: (prev: TrainingSystemState) => TrainingSystemState) => {
    setAppState(prev => {
      const newTrainingSystem = updater(prev.trainingSystem);
      saveTrainingSystemState(newTrainingSystem);
      return {
        ...prev,
        trainingSystem: newTrainingSystem
      };
    });
  };

  /**
   * Clear all errors (MEMOIZED)
   */
  const clearErrors = useCallback(() => {
    setAppState(prev => ({ ...prev, errors: [] }));
  }, []);

  /**
   * SPRINT 1: Handle training mode change
   */
  const handleModeChange = (mode: TrainingMode) => {
    setAppMode('training');
    console.log('🔄 Training mode changed:', { from: appState.trainingSystem.mode, to: mode });
    updateTrainingSystemState(prev => ({
      ...prev,
      mode
    }));
  };

  /**
   * SPRINT 1: Handle custom config changes
   */
  const handleCustomConfigChange = (field: keyof CustomTrainingConfig, value: any) => {
    console.log('⚙️ Custom config changed:', { field, value });
    updateTrainingSystemState(prev => ({
      ...prev,
      customConfig: {
        ...prev.customConfig,
        [field]: value
      }
    }));
  };

  /**
   * SPRINT 2: Handle course day selection with proper validation
   */
  const handleCourseDaySelect = (levelNumber: number, dayNumber: number) => {
    if (!firebaseUser) {
      setShowLoginModal(true);
      return;
    }
    console.log('📅 Course day selection requested:', dayNumber);

    // Prevent rapid state updates
    if (dayNumber === appState.trainingSystem.courseProgress.currentDay && levelNumber === appState.trainingSystem.courseProgress.currentLevel) {
      console.log('Day/Level already selected, ignoring');
      return;
    }

    // Validate level is unlocked
    const levelData = appState.trainingSystem.courseProgress.levelProgress?.[levelNumber];
    if (!levelData || !levelData.unlocked) {
      console.log('Level not unlocked, ignoring selection');
      return;
    }

    // Validate day is unlocked
    const isUnlocked = levelData.unlockedDays.includes(dayNumber);
    if (!isUnlocked) {
      console.log('Day not unlocked, ignoring selection');
      return;
    }

    updateTrainingSystemState(prev => ({
      ...prev,
      courseProgress: {
        ...prev.courseProgress,
        currentLevel: levelNumber,
        currentDay: dayNumber,
        currentRound: 1 // Reset to first round when changing days
      }
    }));
  };

  /**
   * SPRINT 2: Handle course round selection with proper validation
   */
  const handleCourseRoundSelect = (levelNumber: number, roundNumber: number) => {
    if (!firebaseUser) {
      setShowLoginModal(true);
      return;
    }
    console.log('🎯 Course round selection requested:', roundNumber);

    // Prevent rapid state updates
    if (roundNumber === appState.trainingSystem.courseProgress.currentRound && levelNumber === appState.trainingSystem.courseProgress.currentLevel) {
      console.log('Round/Level already selected, ignoring');
      return;
    }

    // Validate round is available (round 1 or previous round completed)
    const levelData = appState.trainingSystem.courseProgress.levelProgress?.[levelNumber];
    if (!levelData) return;

    const dayProgress = levelData.dayProgress.find(
      (dp: any) => dp.dayNumber === appState.trainingSystem.courseProgress.currentDay
    );

    if (dayProgress && roundNumber > 1) {
      const previousRound = dayProgress.rounds.find((r: any) => r.roundNumber === roundNumber - 1);
      if (!previousRound?.completed) {
        console.log('Previous round not completed, ignoring selection');
        return;
      }
    }

    updateTrainingSystemState(prev => ({
      ...prev,
      courseProgress: {
        ...prev.courseProgress,
        currentLevel: levelNumber,
        currentRound: roundNumber
      }
    }));
  };


  /**
   * 🔧 SPRINT 3 TASK 3.1: Batched course round completion - NO MORE CASCADING UPDATES
   */
  const handleCourseRoundCompleteBatched = useCallback(async (dayNumber: number, roundNumber: number, stats: SessionStats) => {
    // Prevent concurrent completion processing
    if (completionInProgressRef.current) {
      console.log('⚠️ Course completion already in progress, storing pending completion');
      pendingCompletionRef.current = { dayNumber, roundNumber, stats };
      return;
    }

    completionInProgressRef.current = true;
    console.log('📋 Course round completion:', { dayNumber, roundNumber });

    try {
      const CourseProgressManager = (await import('@/services/CourseProgressManager')).default;
      const manager = CourseProgressManager.getInstance();
      const updatedProgress = await manager.completeRound(dayNumber, roundNumber, stats);

      // Single atomic state update
      setAppState(prev => ({
        ...prev,
        trainingSystem: {
          ...prev.trainingSystem,
          courseProgress: updatedProgress
        }
      }));

      // Also save non-progress config to localStorage
      saveTrainingSystemState({
        ...currentAppStateRef.current!.trainingSystem,
        courseProgress: updatedProgress
      });

      console.log('✅ Course round completion successful');
    } catch (error) {
      console.error('❌ Course completion error:', error);
    } finally {
      setTimeout(() => {
        completionInProgressRef.current = false;

        if (pendingCompletionRef.current) {
          const pending = pendingCompletionRef.current;
          pendingCompletionRef.current = null;
          handleCourseRoundCompleteBatched(pending.dayNumber, pending.roundNumber, pending.stats);
        }
      }, 100);
    }
  }, []);







  /**
   * 🔧 SPRINT 3: Enhanced session completion with comprehensive monitoring
   */
  const handleSessionComplete = useCallback((sessionData: any) => {
    console.log('🎆 SPRINT 3: Session completion requested:', {
      hasStats: !!sessionData.stats,
      hasOriginalContext: !!sessionData.originalContext,
      completionInProgress: completionInProgressRef.current,
      sessionData
    });

    // 🔧 CRITICAL FIX: Clear red background overlay on session completion
    try {
      const { clearRedBackground } = require('@/components/GameScreen/FeedbackSystem');
      clearRedBackground();
      console.log('✅ Red background overlay cleared on session completion');
    } catch (error) {
      console.warn('⚠️ Failed to clear red background:', error);
    }

    // 🔧 CRITICAL FIX: Use centralized completion state management
    const completionExecuted = completionStateManager.executeCompletion(() => {
      // 🚨 SPRINT 2 FIX: Use ref to access current state without dependencies
      const currentState = currentAppStateRef.current;
      if (!currentState) {
        console.warn('⚠️ No current app state available for session completion');
        return;
      }

      const { mode } = currentState.trainingSystem; // Use ref instead of direct state access

      if (mode === 'course') {
        // 🔧 CRITICAL FIX: Get context from session context manager
        const sessionContext = sessionContextManager.completeSession();

        if (sessionContext && sessionContextManager.validateContext(sessionContext)) {
          const { dayNumber, roundNumber } = sessionContext;
          console.log(`✅ Using preserved session context: Day ${dayNumber}, Round ${roundNumber}`);
          handleCourseRoundCompleteBatched(dayNumber, roundNumber, sessionData.stats || sessionData);
        } else if (sessionData.originalContext) {
          // Fallback to session data context
          const { dayNumber, roundNumber } = sessionData.originalContext;
          console.log(`✅ Using session data context: Day ${dayNumber}, Round ${roundNumber}`);
          handleCourseRoundCompleteBatched(dayNumber, roundNumber, sessionData.stats || sessionData);
        } else {
          // Last resort: use current state
          console.warn('⚠️ No preserved context, using current state as last resort');
          const { courseProgress } = currentState.trainingSystem;
          handleCourseRoundCompleteBatched(courseProgress.currentDay, courseProgress.currentRound, sessionData.stats || sessionData);
        }
      }

      // Clear context after successful completion
      sessionContextManager.clearContext();
    });

    if (!completionExecuted) {
      console.log('⚠️ Session completion was rejected (in progress or too soon)');
    }
  }, []);

  /**
   * Handle navigation back to menu (placeholder for future phases)
   */
  const handleBackToMenu = () => {
    console.log('Back to menu requested');
    // For now, just log the request
    // In future phases, this would navigate to a proper menu screen
  };

  /**
   * 🔧 ADMIN PANEL: Close admin panel
   */
  const handleCloseAdminPanel = () => {
    console.log('🔙 Closing Admin Panel');
    setAppState(prev => ({ ...prev, showAdminPanel: false }));
  };

  /**
   * 🔧 ADMIN PANEL: Check if user has admin access
   */
  const hasAdminAccess = (): boolean => {
    // TODO: Implement proper admin role checking when auth roles are added
    // For now, any authenticated user has admin access
    return !!firebaseUser;
  };

  /**
   * Render error messages
   */
  const renderErrors = () => {
    if (appState.errors.length === 0) return null;

    return (
      <div className="error-container">
        {appState.errors.slice(-3).map(error => (
          <div key={error.id} className="error-message">
            <span className="error-type">{error.type}:</span>
            <span className="error-text">{error.message}</span>
            <button
              onClick={() => {
                setAppState(prev => ({
                  ...prev,
                  errors: prev.errors.filter(e => e.id !== error.id)
                }));
              }}
              className="error-close"
            >
              ×
            </button>
          </div>
        ))}
        {appState.errors.length > 3 && (
          <button onClick={clearErrors} className="clear-all-errors">
            Clear all errors
          </button>
        )}
      </div>
    );
  };

  // User avatar dropdown state
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-avatar-container')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Loading screen with Firebase user initialization
  if (!appState.isInitialized || userLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h1>KeyboardHero</h1>
          <p>
            {userLoading
              ? 'Setting up your profile...'
              : 'Loading your typing training system...'
            }
          </p>
          {userError && (
            <div className="loading-error">
              <p>⚠️ Firebase User Error: {userError.message}</p>
              <p style={{ fontSize: '0.75rem', marginTop: '8px' }}>App will continue with limited functionality.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const { trainingSystem } = appState;

  // 🔧 CRITICAL FIX: Ensure courseProgress is always defined
  if (!trainingSystem.courseProgress) {
    console.error('❌ Critical: courseProgress is undefined, resetting to default state');
    const defaultState = getDefaultTrainingSystemState();
    setAppState(prev => ({
      ...prev,
      trainingSystem: defaultState
    }));
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h1>KeyboardHero</h1>
          <p>Repairing training system state...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Global Error Display */}
      {renderErrors()}

      {/* OPTIMIZED: Enhanced Main Header */}
      <div className="main-header">
        {/* Logo Section */}
        <div className="logo-section">
          <img
            src="/assets/logo_KBH.png"
            alt="KeyboardHero"
            className="logo-image"
          />
        </div>

        {/* Left Controls - Only show when NOT on Statistics page */}
        {!isStatisticsPage && (
          <div className="left-controls">
            {/* Vertical Divider */}
            <div className="header-divider"></div>

            {/* 🎯 FINAL CLEANUP: Training info removed from header - moved to Training Widget */}
          </div>
        )}

        {/* Spacer for Statistics page to push right section to the right */}
        {isStatisticsPage && <div className="header-spacer"></div>}

        {/* Center Section - Navigation */}
        <div className="center-section">
          {sessionStatus !== 'active' && (
            <div className="nav-buttons">
              <button
                onClick={() => {
                  setAppMode('training');
                  if (location.pathname !== '/') window.history.pushState({}, '', '/');
                }}
                className={`nav-btn ${!isStatisticsPage && appMode === 'training' ? 'active' : ''}`}
              >
                🎮 Training Mode
              </button>

              <button
                onClick={() => {
                  setAppMode('game');
                  if (location.pathname !== '/') window.history.pushState({}, '', '/');
                }}
                className={`nav-btn game-btn ${appMode === 'game' && !isStatisticsPage ? 'active' : ''}`}
              >
                🎸 Game Mode
              </button>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="right-section">
          {/* Link to landing page */}
          <a href="https://keyboardhe.ro" target="_blank" rel="noreferrer" className="power-pinky-link hidden sm:block">
            Power Pinky Method &copy;
          </a>

          <div className="header-divider-auth h-6 w-[1px] bg-white/20 mx-4 hidden sm:block"></div>

          {/* Session Control Buttons - Only show during active session */}
          {sessionStatus === 'active' && (
            <div className="session-controls">
              <button
                className="control-btn hint"
                onClick={() => setShowFingerHints(!showFingerHints)}
                title={showFingerHints ? 'Hide finger hints' : 'Show finger hints'}
              >
                {showFingerHints ? '👁️ Hide' : '👁️ Hint'}
              </button>
              <button
                className="control-btn pause"
                onClick={handlePauseResumeFixed}
              >
                {sessionStatus === 'active' ? '⏸️ Pause' : '▶️ Resume'}
              </button>
              <button
                className="control-btn stop"
                onClick={handleStopSessionFixed}
              >
                ⏹️ Stop
              </button>
            </div>
          )}

          {/* User Avatar with Dropdown */}
          <div className="user-avatar-container">
            {!firebaseUser ? (
              <button className="nav-btn" onClick={() => setShowLoginModal(true)}>
                Sign In
              </button>
            ) : (
              <>
                <button
                  className="user-avatar-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <img
                    src="/assets/male.png"
                    alt="User Avatar"
                    className="avatar-image"
                  />
                </button>

                {/* Dropdown Menu */}
                {showUserDropdown && (
                  <div className="user-dropdown">
                    <Link
                      to="/statistics"
                      className="dropdown-item"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      📊 Statistics
                    </Link>
                    <button
                      className="dropdown-item signout-btn"
                      onClick={async () => {
                        setShowUserDropdown(false);

                        // 🧹 Clear user-specific localStorage before signing out
                        if (firebaseUser?.uid) {
                          const storageKey = getLocalStorageKey(firebaseUser.uid);
                          localStorage.removeItem(storageKey);
                          console.log('🧹 Cleared user localStorage on logout');
                        }

                        const { signOutUser } = await import('@/firebase');
                        await signOutUser();
                        window.location.reload();
                      }}
                    >
                      🚪 Sign Out
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="app-main">
        <Routes>
          <Route path="/" element={
            appMode === 'game' ? (
              <GuitarHeroGame />
            ) : (
              <GameScreen
                // 🔧 SPRINT 2 TASK 2.1: Removed bidirectional sync props
                trainingMode={trainingSystem.mode}
                showFingerHints={showFingerHints}
                sessionStatus={sessionStatus}
                // REMOVED: isSessionActiveFromApp={isSessionActive}
                onSessionStatusChange={setSessionStatus}
                // REMOVED: onSessionActiveChange={setIsSessionActive}
                {...(trainingSystem.mode === 'oneWord' ? {
                  // One Word Training mode: GameScreen derives fingerConfig from typed word.
                  // We still need to send custom-mode-shaped props so the engine plumbing has defaults.
                  dayNumber: 1,
                  handMode: 'both' as const,
                  complexityMode: 'words' as const,
                  customFingers: [],
                  uid: firebaseUser?.uid || undefined,
                } : trainingSystem.mode === 'custom' ? {
                  // Custom mode props
                  dayNumber: 1,
                  handMode: trainingSystem.customConfig.handMode,
                  complexityMode: trainingSystem.customConfig.complexityMode,
                  customFingers: trainingSystem.customConfig.selectedFingers
                } : {
                  // Course mode props
                  levelNumber: trainingSystem.courseProgress.currentLevel || 1,
                  dayNumber: trainingSystem.courseProgress.currentDay,
                  roundNumber: trainingSystem.courseProgress.currentRound,
                  mode: 'course' as const,
                  courseProgress: trainingSystem.courseProgress,
                  onCourseDaySelect: handleCourseDaySelect,
                  onCourseRoundSelect: handleCourseRoundSelect
                })}
                onSessionComplete={handleSessionComplete}
                onBackToMenu={handleBackToMenu}
                onRequestLogin={() => setShowLoginModal(true)}
                isAuthenticated={!!firebaseUser}
                onFingerSelectionChange={(fingers) => {
                  console.log('🎯 PHASE 2: Finger selection changed from GameScreen:', fingers);
                  handleCustomConfigChange('selectedFingers', fingers);

                  // Derive and update handMode from selected fingers
                  const newHandMode = deriveHandMode(fingers);
                  handleCustomConfigChange('handMode', newHandMode);
                }}
                onModeChange={handleModeChange}
                onComplexityChange={(mode) => handleCustomConfigChange('complexityMode', mode)}
                ref={handleGameScreenRef}
              />
            )
          } />
          <Route path="/statistics" element={
            firebaseUser ? <StatisticsDashboard /> : (
              <div className="access-denied">
                <h2>🔒 Login Required</h2>
                <p>Please sign in to view your detailed statistics.</p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-bold transition-colors"
                >
                  Sign In
                </button>
              </div>
            )
          } />
          <Route path="/admin" element={
            hasAdminAccess() ? (
              <AdminDashboard onClose={() => window.history.back()} />
            ) : (
              <div className="access-denied">
                <h2>🔒 Access Denied</h2>
                <p>You don't have permission to access the admin panel.</p>
              </div>
            )
          } />
        </Routes>
      </main>

      {/* 🔧 ADMIN PANEL: Fullscreen overlay when active */}
      {appState.showAdminPanel && (
        <div className="admin-panel-fullscreen">
          <AdminDashboard onClose={handleCloseAdminPanel} />
        </div>
      )}

      <style>{`
        .app {
          width: 100vw;
          height: 100vh;
          background: #0f172a;
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          position: relative;
        }

        .loading-screen {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100vw;
          height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }

        .loading-content {
          text-align: center;
          padding: 40px;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(59, 130, 246, 0.3);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-content h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 0 0 10px 0;
          background: linear-gradient(45deg, #3b82f6, #22c55e);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .loading-content p {
          color: #9ca3af;
          margin: 0;
        }

        .loading-error {
          margin-top: 16px;
          padding: 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          max-width: 400px;
        }

        .loading-error p {
          color: #fca5a5 !important;
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .error-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-width: 400px;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: rgba(239, 68, 68, 0.9);
          border: 1px solid rgba(220, 38, 38, 0.8);
          border-radius: 8px;
          color: white;
          font-size: 0.875rem;
          backdrop-filter: blur(10px);
          animation: slideInRight 0.3s ease-out;
        }

        .error-type {
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
        }

        .error-text {
          flex: 1;
        }

        .error-close {
          background: none;
          border: none;
          color: white;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .error-close:hover {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
        }

        .clear-all-errors {
          padding: 8px 12px;
          background: rgba(75, 85, 99, 0.9);
          border: 1px solid rgba(107, 114, 128, 0.8);
          border-radius: 6px;
          color: white;
          font-size: 0.75rem;
          cursor: pointer;
          align-self: flex-end;
        }

        .clear-all-errors:hover {
          background: rgba(107, 114, 128, 0.9);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .main-header {
          position: fixed;
          top: 20px;
          left: 20px;
          right: 20px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px 20px;
          background: rgba(17, 24, 39, 0.95);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 12px;
          backdrop-filter: blur(20px);
          min-height: 60px;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 0 0 auto;
        }

        .logo-image {
          height: 50px;
          width: auto;
        }

        .logo-text {
          font-size: 1.5rem;
          font-weight: 700;
          color: #3b82f6;
        }

        .left-controls {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .header-spacer {
          flex: 1;
        }

        .right-section {
          display: flex;
          align-items: center;
          gap: 0px;
          flex: 0 0 auto;
          margin-left: auto;
        }

        .center-section {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .power-pinky-link {
          color: #ffffff;
          font-size: 1.1rem;
          font-weight: 700;
          text-decoration: none;
          transition: color 0.2s;
        }

        .power-pinky-link:hover {
          color: #ff00ea;
        }

        .header-divider-auth {
          width: 1px;
          height: 24px;
          background-color: rgba(255, 255, 255, 0.2);
          margin: 0 16px;
        }

        .nav-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .nav-btn {
          padding: 10px 16px;
          border-radius: 8px;
          color: #9ca3af;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.2s ease-out;
          border: 1px solid transparent;
          background: rgba(75, 85, 99, 0.3);
          cursor: pointer;
        }

        .nav-btn:hover {
          color: #ffffff;
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.4);
          transform: translateY(-1px);
        }

        .nav-btn.active {
          color: #ffffff;
          background: rgba(59, 130, 246, 0.8);
          border-color: rgba(59, 130, 246, 0.6);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .nav-btn.game-btn {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
          border-color: rgba(34, 197, 94, 0.3);
        }

        .nav-btn.game-btn:hover {
          background: rgba(34, 197, 94, 0.3);
          border-color: rgba(34, 197, 94, 0.5);
          transform: translateY(-1px);
        }
        
        .nav-btn.admin-btn {
          background: rgba(251, 146, 60, 0.2);
          color: #fb923c;
          border-color: rgba(251, 146, 60, 0.3);
        }
        
        .nav-btn.admin-btn:hover {
          background: rgba(251, 146, 60, 0.3);
          border-color: rgba(251, 146, 60, 0.5);
          transform: translateY(-1px);
        }

        .session-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .user-avatar-container {
          position: relative;
        }

        .user-avatar-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 2px solid rgba(59, 130, 246, 0.6);
          background: rgba(59, 130, 246, 0.1);
          cursor: pointer;
          transition: all 0.2s ease-out;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .user-avatar-btn:hover {
          border-color: rgba(59, 130, 246, 0.8);
          background: rgba(59, 130, 246, 0.2);
          transform: scale(1.05);
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .user-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: rgba(17, 24, 39, 0.95);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 8px;
          backdrop-filter: blur(20px);
          min-width: 160px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
          z-index: 1001;
        }

        .dropdown-item {
          display: block;
          width: 100%;
          padding: 12px 16px;
          color: #e5e7eb;
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;
          border: none;
          background: none;
          cursor: pointer;
          transition: all 0.2s ease-out;
          text-align: left;
        }

        .dropdown-item:hover {
          background: rgba(59, 130, 246, 0.2);
          color: #ffffff;
        }

        .dropdown-item.signout-btn {
          color: #ef4444;
          border-top: 1px solid rgba(75, 85, 99, 0.3);
        }

        .dropdown-item.signout-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ffffff;
        }

        .training-info-bar {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
          min-width: 0;
        }

        .training-mode-label {
          font-size: 1rem;
          font-weight: 700;
          color: #ffffff;
        }

        .training-details {
          font-size: 0.75rem;
          color: #9ca3af;
          font-style: italic;
        }

        .mode-toggle-btn {
          padding: 8px 16px;
          background: rgba(59, 130, 246, 0.8);
          border: 1px solid rgba(59, 130, 246, 0.6);
          border-radius: 8px;
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease-out;
          flex: 0 0 auto;
        }

        .mode-toggle-btn:hover {
          background: rgba(59, 130, 246, 1);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .mode-toggles {
          display: flex;
          gap: 8px;
          align-items: center;
          flex: 0 0 auto;
        }

        .reset-btn-header {
          padding: 6px 12px;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.5);
          border-radius: 6px;
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease-out;
          flex: 0 0 auto;
        }

        .reset-btn-header:hover {
          background: rgba(239, 68, 68, 0.3);
          transform: translateY(-1px);
        }

        .song-selector-header {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(34, 197, 94, 0.2);
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid rgba(34, 197, 94, 0.5);
        }

        .song-selector-header label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #22c55e;
        }

        .song-dropdown-header {
          background: rgba(17, 24, 39, 0.9);
          color: white;
          border: 1px solid rgba(34, 197, 94, 0.5);
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 0.875rem;
          cursor: pointer;
          min-width: 160px;
        }

        .song-dropdown-header:focus {
          outline: none;
          border-color: rgba(34, 197, 94, 0.8);
          box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
        }

        .song-dropdown-header option {
          background: rgba(17, 24, 39, 1);
          color: white;
          padding: 8px;
        }

        .guitar-hero-fullscreen {
          position: fixed;
          top: 80px; /* Below header */
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9998;
          background: #000;
        }
        
        .admin-panel-fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9999; /* Higher than guitar hero */
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
        }

        .header-divider {
          width: 1px;
          height: 32px;
          background: rgba(75, 85, 99, 0.5);
          flex: 0 0 auto;
        }

        .control-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease-out;
        }

        .control-btn.hint {
          background: linear-gradient(45deg, #8b5cf6, #7c3aed);
          color: white;
        }

        .control-btn.pause {
          background: linear-gradient(45deg, #f59e0b, #d97706);
          color: white;
        }

        .control-btn.stop {
          background: linear-gradient(45deg, #ef4444, #dc2626);
          color: white;
        }

        .control-btn.restart {
          background: linear-gradient(45deg, #3b82f6, #2563eb);
          color: white;
        }

        .control-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .app-main {
          width: 100%;
          min-height: calc(100vh - 100px);
          padding-top: 100px;
        }

        .placeholder-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          text-align: center;
          gap: 20px;
        }

        .placeholder-screen h2 {
          font-size: 2rem;
          margin: 0;
          color: #3b82f6;
        }

        .placeholder-screen p {
          color: #9ca3af;
          margin: 0;
        }

        .placeholder-screen button {
          padding: 12px 24px;
          background: linear-gradient(45deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s ease-out;
        }

        .placeholder-screen button:hover {
          transform: translateY(-2px);
        }
        
        .access-denied {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          text-align: center;
          gap: 20px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }
        
        .access-denied h2 {
          font-size: 2rem;
          margin: 0;
          color: #ef4444;
        }
        
        .access-denied p {
          color: #9ca3af;
          margin: 0;
          font-size: 1.1rem;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .error-container {
            right: 10px;
            top: 10px;
            max-width: calc(100vw - 20px);
          }

          .main-header {
            left: 10px;
            right: 10px;
            top: 10px;
            flex-wrap: wrap;
            gap: 12px;
            padding: 16px;
            min-height: auto;
          }

          .left-controls {
            flex-wrap: wrap;
            gap: 8px;
          }

          .mode-toggles {
            gap: 6px;
          }

          .nav-buttons {
            gap: 6px;
          }

          .nav-btn {
            padding: 8px 12px;
            font-size: 0.8rem;
          }

          .logo-text {
            font-size: 1.2rem;
          }

          .logo-image {
            height: 32px;
          }

          .training-mode-label {
            font-size: 0.875rem;
          }

          .training-details {
            font-size: 0.675rem;
          }

          .session-controls {
            gap: 6px;
          }

          .user-avatar-btn {
            width: 36px;
            height: 36px;
          }

          .app-main {
            padding-top: 140px; /* Adjusted for wrapped header */
          }
        }
        
        /* 🎯 PHASE 2: Hand picker styles moved to GameScreen - header now only has complexity toggle */
        .guitar-hero-disabled-controls {
          padding: 8px 16px;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.5);
          border-radius: 8px;
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 600;
          text-align: center;
        }

        .login-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.8);
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          backdrop-filter: blur(8px);
        }
      `}</style>

      {/* Login Modal Overlay */}
      {showLoginModal && (
        <div className="login-modal-overlay">
          <LoginPage
            onClose={() => setShowLoginModal(false)}
            onSuccess={() => setShowLoginModal(false)}
            className="login-modal-content"
          />
        </div>
      )}
    </div>
  );
};

/**
 * Main App Component with Authentication Wrapper
 * 
 * Wraps the application with Firebase Auth Provider and handles
 * the authentication flow with login/logout states.
 */
function App() {
  return (
    <Router>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </Router>
  );
}

export default App;
